import { useRef, useState } from "react";
import { Upload, Trash2, ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";

/**
 * Admin mockup gallery — upload, preview, delete.
 * Props:
 *   contractId: string (required)
 *   images: MockupImage[]
 *   onChange: (images) => void   // called after add/remove
 */
export default function MockupGallery({ contractId, images = [], onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const token = getToken();

  function srcFor(img) {
    return `${API}/contracts/${contractId}/mockups/${img.id}?auth=${encodeURIComponent(token || "")}`;
  }

  async function handleFiles(fileList) {
    if (!contractId) {
      toast.error("Save the contract first");
      return;
    }
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setUploading(true);
    let next = [...images];
    let failures = 0;
    for (const f of files) {
      if (!f.type.startsWith("image/")) {
        failures++;
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/contracts/${contractId}/mockups`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(data);
      } catch (e) {
        failures++;
        toast.error(e?.response?.data?.detail || `Failed: ${f.name}`);
      }
    }
    setUploading(false);
    onChange?.(next);
    if (fileRef.current) fileRef.current.value = "";
    if (files.length - failures > 0) toast.success(`${files.length - failures} image(s) uploaded`);
  }

  async function remove(img) {
    if (!window.confirm("Remove this mockup?")) return;
    try {
      await api.delete(`/contracts/${contractId}/mockups/${img.id}`);
      onChange?.(images.filter((m) => m.id !== img.id));
      toast.success("Mockup removed");
    } catch {
      toast.error("Failed to remove");
    }
  }

  return (
    <div data-testid="mockup-gallery">
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || !contractId}
          data-testid="upload-mockup-btn"
          className="inline-flex items-center gap-2 border border-[#C9963F]/50 text-[#C9963F] hover:bg-[#C9963F]/10 px-3 py-2 text-sm disabled:opacity-50 transition-colors"
        >
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload Images"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="mockup-file-input"
        />
        <div className="text-xs text-white/40">JPG, PNG, WEBP · up to 10MB each</div>
      </div>

      {!contractId && (
        <div className="text-xs text-white/40">Save the contract first to attach mockup images.</div>
      )}

      {contractId && images.length === 0 && (
        <div className="border border-dashed border-white/15 px-6 py-10 text-center text-white/40 flex flex-col items-center gap-2">
          <ImageIcon size={22} className="opacity-60" />
          <div className="text-xs uppercase tracking-wider">No mockups attached yet</div>
        </div>
      )}

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((img) => (
            <div
              key={img.id}
              className="relative group border border-white/10 bg-[#0a0a0a] overflow-hidden aspect-square"
              data-testid={`mockup-thumb-${img.id}`}
            >
              <img
                src={srcFor(img)}
                alt={img.filename}
                onClick={() => setPreviewSrc(srcFor(img))}
                className="w-full h-full object-cover cursor-zoom-in transition-transform group-hover:scale-105"
              />
              <button
                type="button"
                onClick={() => remove(img)}
                data-testid={`remove-mockup-${img.id}`}
                className="absolute top-1.5 right-1.5 p-1.5 bg-black/80 border border-white/20 text-red-400 hover:text-red-300 hover:border-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove"
              >
                <Trash2 size={12} />
              </button>
              <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-gradient-to-t from-black/90 to-transparent text-[10px] text-white/70 truncate">
                {img.filename}
              </div>
            </div>
          ))}
        </div>
      )}

      {previewSrc && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => setPreviewSrc(null)}
          data-testid="mockup-lightbox"
        >
          <button
            className="absolute top-5 right-5 text-white/70 hover:text-white"
            onClick={() => setPreviewSrc(null)}
            data-testid="close-lightbox"
          >
            <X size={24} />
          </button>
          <img src={previewSrc} alt="preview" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}
